import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FiPlusCircle, FiArrowUpRight, FiClock, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

const STATUS_STYLES = {
  AWAITING_FUNDS: { bg: 'bg-status-yellow-bg', text: 'text-status-yellow', label: 'Awaiting Funds' },
  FUNDS_RECEIVED: { bg: 'bg-status-blue-bg', text: 'text-status-blue', label: 'Funds Received' },
  PROCESSING:     { bg: 'bg-status-purple-bg', text: 'text-status-purple', label: 'Processing' },
  SENT:           { bg: 'bg-status-blue-bg', text: 'text-status-blue', label: 'Sent' },
  COMPLETED:      { bg: 'bg-status-green-bg', text: 'text-status-green', label: 'Completed' },
  FAILED:         { bg: 'bg-status-red-bg', text: 'text-status-red', label: 'Failed' },
};

const formatCurrency = (amount, currency) => {
  if (amount == null) return '—';
  const n = typeof amount === 'number' ? amount : parseFloat(amount);
  if (isNaN(n)) return '—';
  const fmt = n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (currency === 'NGN') return '₦' + fmt;
  if (currency === 'CFA' || currency === 'XOF') return 'CFA ' + fmt;
  return fmt + ' ' + (currency || '');
};

const formatDate = (d) => {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function Dashboard({ merchantData }) {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const fns = getFunctions();
      const merchantApi = httpsCallable(fns, 'merchantApi');

      const [txResult, rateResult] = await Promise.all([
        merchantApi({ action: 'getMerchantTransactions', limit: 10 }),
        merchantApi({ action: 'getMerchantRates' }),
      ]);

      setTransactions(txResult.data.transactions || []);
      setRates(rateResult.data.rates || []);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Stats
  const stats = {
    total: transactions.length,
    volume: transactions.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0),
    pending: transactions.filter(t => ['AWAITING_FUNDS', 'FUNDS_RECEIVED', 'PROCESSING', 'SENT'].includes(t.status)).length,
    completed: transactions.filter(t => t.status === 'COMPLETED').length,
  };

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-main">
            Welcome, {merchantData?.fullName || 'Merchant'}
          </h1>
          <p className="text-text-secondary text-sm mt-1">Manage your OTC transactions</p>
        </div>
        <button
          onClick={() => navigate('/create')}
          className="flex items-center gap-2 bg-primary text-white px-5 py-3 rounded-xl font-semibold text-sm hover:bg-primary-light transition-colors shadow-sm w-full sm:w-auto justify-center"
        >
          <FiPlusCircle size={18} />
          New Transaction
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={<FiArrowUpRight />} label="Total Transactions" value={stats.total} color="text-primary" />
        <StatCard icon={<FiClock />} label="Total Volume" value={formatCurrency(stats.volume, 'NGN')} color="text-status-yellow" />
        <StatCard icon={<FiAlertCircle />} label="Pending" value={stats.pending} color="text-status-blue" />
        <StatCard icon={<FiCheckCircle />} label="Completed" value={stats.completed} color="text-status-green" />
      </div>

      {/* Current Rates */}
      {rates.length > 0 && (
        <div className="bg-surface rounded-xl shadow-card p-5">
          <h2 className="text-sm font-semibold text-text-main mb-3">Merchant Rates</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rates.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 bg-background rounded-lg">
                <span className="text-sm font-medium text-text-main">
                  {r.from} → {r.to}
                </span>
                <span className="text-sm font-bold text-primary">{r.rate}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      <div className="bg-surface rounded-xl shadow-card">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-text-main">Recent Transactions</h2>
          <button
            onClick={() => navigate('/transactions')}
            className="text-xs font-medium text-primary hover:underline"
          >
            View all
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-text-secondary text-sm">Loading...</div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-text-secondary text-sm">No transactions yet</p>
            <button
              onClick={() => navigate('/create')}
              className="mt-3 text-sm font-medium text-primary hover:underline"
            >
              Create your first transaction
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {transactions.slice(0, 5).map((tx) => {
              const style = STATUS_STYLES[tx.status] || STATUS_STYLES.AWAITING_FUNDS;
              return (
                <div
                  key={tx.id}
                  onClick={() => navigate(`/transactions/${tx.id}`)}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text-main truncate">
                      {formatCurrency(tx.amount, tx.inputCurrency)} → {formatCurrency(tx.outputAmount, tx.outputCurrency)}
                    </p>
                    <p className="text-xs text-text-secondary mt-0.5">
                      {tx.reference} · {formatDate(tx.createdAt)}
                    </p>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${style.bg} ${style.text} whitespace-nowrap ml-3`}>
                    {style.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-surface rounded-xl shadow-card p-3 sm:p-4">
      <div className={`${color} mb-1.5 sm:mb-2`}>{icon}</div>
      <p className="text-lg sm:text-2xl font-bold text-text-main truncate">{value}</p>
      <p className="text-[10px] sm:text-xs text-text-secondary mt-0.5 leading-tight">{label}</p>
    </div>
  );
}
